import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Container, Dialog, Divider, MenuItem, Tab, Tabs } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import Text from '../../components/UI/atoms/Text';
import Card from '../../components/UI/molecules/Card';
import Button from '../../components/UI/atoms/Button';
import Input from '../../components/UI/atoms/Input';
import SubscriptionTable from './components/SubscriptionTable';
import CreateManualSubModal from './components/CreateManualSubModal';
import UserTable from './components/UserTable';
import AdminNav from './components/AdminNav';
import { authPost, authGet } from '../../services/authCalls';
import { useApp } from '../../AppContext';

export default function FirmDetail() {
  const { firmId } = useParams();
  const { handleMessage } = useApp();

  const [firmDetail, setFirmDetail] = useState(null);
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState(false);
  const [dialogPayload, setDialogPayload] = useState(null);
  const [createSubOpen, setCreateSubOpen] = useState(false);
  const [editFirmOpen, setEditFirmOpen] = useState(false);
  const [editFirmForm, setEditFirmForm] = useState({ firmName: '', seatAlloted: 1, subscriptionType: '', accountURL: '', opportunityURL: '' });
  const SUBSCRIPTION_TYPES = ['Digital', 'Print', 'Both'];

  const firmName = useMemo(() => {
    const firm = firmDetail?.firm;
    return firm?.FirmName || firm?.firmName || firm?.firmName || '';
  }, [firmDetail]);

  const closeDialog = () => {
    setOpen(false);
    setDialogPayload(null);
  };

  const openDialog = (payload) => {
    setDialogPayload(payload);
    setOpen(true);
  };

  const load = () => {
    Promise.all([
      authPost('/api/admin/firm-detail', firmId),
      authPost('/api/admin/subscriptions', firmId),
      authGet('/api/admin/listed-plan'),
    ])
      .then(([detailRes, subRes, planRes]) => {
        setFirmDetail(detailRes.data);
        setSubs(subRes.data || []);
        setPlans(planRes.data || []);
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to load firm detail';
        handleMessage('error', msg);
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [firmId]);

  const handleCancelSubscription = () => {
    closeDialog();
    authPost('/api/admin/subscription/cancel', { firmId })
      .then(() => {
        handleMessage('success', 'Subscription cancelled');
        load();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to cancel subscription';
        handleMessage('error', msg);
      });
  };

  const openEditFirm = () => {
    const f = firmDetail?.firm;
    const sub = firmDetail?.subscription;
    setEditFirmForm({
      firmName: f?.FirmName || f?.firmName || '',
      seatAlloted: f?.SeatAlloted ?? f?.seatAlloted ?? 1,
      subscriptionType: f?.SubscriptionType || f?.subscriptionType || '',
      accountURL: f?.AccountURL || f?.accountURL || '',
      opportunityURL: sub?.OpportunityURL || sub?.opportunityURL || '',
    });
    setEditFirmOpen(true);
  };

  const closeEditFirm = () => {
    setEditFirmOpen(false);
  };

  const saveFirmInfo = () => {
    authPost('/api/admin/firm/update', {
      firmId,
      firmName: editFirmForm.firmName,
      seatAlloted: Number(editFirmForm.seatAlloted) || 1,
      subscriptionType: editFirmForm.subscriptionType,
      accountURL: editFirmForm.accountURL,
      opportunityURL: editFirmForm.opportunityURL,
    })
      .then(() => {
        handleMessage('success', 'Firm info updated');
        closeEditFirm();
        load();
      })
      .catch(err => {
        const msg = err?.response?.data || err?.response?.headers?.errormessage || 'Unable to update firm info';
        handleMessage('error', msg);
      });
  };

  const firm = firmDetail?.firm;
  const tags = firm?.SubscriptionTag || firm?.subscriptionTag || [];
  const userList = firmDetail?.userList || [];
  const rootUser = userList.find(u => u?.IsRootUser || u?.isRootUser);
  const rootEmail = rootUser?.Email || rootUser?.email || '—';
  const userCount = userList.length;

  return (
    <Container maxWidth={false} sx={{ py: '32px', px: { xs: '16px', md: '40px' } }}>
      <AdminNav />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
        <Link to='/admin/firms' style={{ textDecoration: 'none' }}>
          <Text sx={{ fontSize: '13px', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}>← All Firms</Text>
        </Link>
      </Box>

      <Box sx={{ mb: '20px' }}>
        <Text type='h1'>{firmName || firmId}</Text>
      </Box>

      <Card sx={{ margin: 0, mb: '16px' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: '16px', alignItems: 'start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Firm ID</Text>
            <Text sx={{ fontSize: '13px', fontFamily: 'monospace', mt: '2px', wordBreak: 'break-all' }}>{firmId}</Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Firm Name</Text>
            <Text sx={{ fontSize: '13px', mt: '2px', wordBreak: 'break-word' }}>{firm?.FirmName || firm?.firmName || '—'}</Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Email</Text>
            <Text sx={{ fontSize: '13px', mt: '2px', wordBreak: 'break-all' }}>{rootEmail}</Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Current Subscription ID</Text>
            <Text sx={{ fontSize: '13px', fontFamily: 'monospace', mt: '2px', wordBreak: 'break-all' }}>{firm?.CurrentSubscriptionId || firm?.currentSubscriptionId || '—'}</Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Subscription Type</Text>
            <Text sx={{ fontSize: '13px', mt: '2px' }}>{firm?.ShippingPreference || firm?.shippingPreference || firm?.SubscriptionType || firm?.subscriptionType || '—'}</Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Users</Text>
            <Text sx={{ fontSize: '13px', mt: '2px' }}>{userCount}</Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Seats</Text>
            <Text sx={{ fontSize: '13px', mt: '2px' }}>{firm?.SeatAlloted ?? firm?.seatAlloted ?? '—'}</Text>
          </Box>
          {tags.length > 0 && (
            <Box sx={{ minWidth: 0 }}>
              <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', mb: '4px' }}>Plan</Text>
              <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {tags.map((t, i) => <Chip key={i} label={t} size='small' color='primary' variant='outlined' />)}
              </Box>
            </Box>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Account URL</Text>
            <Text sx={{ fontSize: '13px', mt: '2px', wordBreak: 'break-all' }}>
              {firm?.AccountURL || firm?.accountURL ? (
                <a href={firm?.AccountURL || firm?.accountURL} target='_blank' rel='noopener noreferrer' style={{ color: 'inherit' }}>{firm?.AccountURL || firm?.accountURL}</a>
              ) : '—'}
            </Text>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Text sx={{ fontSize: '11px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Opportunity URL</Text>
            <Text sx={{ fontSize: '13px', mt: '2px', wordBreak: 'break-all' }}>
              {firmDetail?.subscription?.OpportunityURL || firmDetail?.subscription?.opportunityURL ? (
                <a href={firmDetail?.subscription?.OpportunityURL || firmDetail?.subscription?.opportunityURL} target='_blank' rel='noopener noreferrer' style={{ color: 'inherit' }}>{firmDetail?.subscription?.OpportunityURL || firmDetail?.subscription?.opportunityURL}</a>
              ) : '—'}
            </Text>
          </Box>
          <Box sx={{ gridColumn: { md: 'span 1' }, display: 'flex', gap: '8px', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
            <Button type='outlined' onClick={openEditFirm}>Edit Firm</Button>
            <Button onClick={() => setCreateSubOpen(true)}>+ Create Manual Sub</Button>
          </Box>
        </Box>
      </Card>

      <Card sx={{ margin: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: '4px' }}>
          <Tab label={`Subscriptions (${subs.length})`} />
          <Tab label={`Users (${userCount})`} />
        </Tabs>
        <Divider sx={{ mb: '16px' }} />

        {tab === 0 && (
          <SubscriptionTable
            subscriptions={subs}
            plans={plans}
            onCancel={(sub) => openDialog(sub)}
          />
        )}

        {tab === 1 && (
          <UserTable
            firmId={firmId}
            users={firmDetail?.userList || []}
            onRefresh={() => load()}
          />
        )}
      </Card>

      <CreateManualSubModal
        open={createSubOpen}
        onClose={() => setCreateSubOpen(false)}
        firmId={firmId}
        plans={plans}
        onSuccess={() => load()}
      />

      <Dialog onClose={closeDialog} open={open}>
        <Card sx={{ margin: 0, width: { xs: '320px', sm: '480px' } }}>
          <Text type='h1' sx={{ mb: '12px' }}>Cancel Subscription</Text>
          <Divider sx={{ mb: '16px' }} />
          <Text sx={{ mb: '8px' }}>This will cancel the active Stripe subscription for this firm.</Text>
          <Text sx={{ fontSize: '13px', color: 'text.secondary', fontFamily: 'monospace', mb: '20px' }}>{firmId}</Text>
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button type='outlined' onClick={() => closeDialog()}>Cancel</Button>
            <Button onClick={() => handleCancelSubscription()}>Confirm Cancel</Button>
          </Box>
        </Card>
      </Dialog>

      <Dialog onClose={closeEditFirm} open={editFirmOpen}>
        <Card sx={{ margin: 0, width: { xs: '320px', sm: '520px' } }}>
          <Text type='h1' sx={{ mb: '12px' }}>Edit Firm Info</Text>
          <Divider sx={{ mb: '16px' }} />
          <Input label='Firm Name' id='edit-firmName' value={editFirmForm.firmName} onChange={e => setEditFirmForm(prev => ({ ...prev, firmName: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: '12px' }}>
            <Box sx={{ flex: 1 }}>
              <Input label='Seat Count' id='edit-seatAlloted' type='number' value={editFirmForm.seatAlloted} onChange={e => setEditFirmForm(prev => ({ ...prev, seatAlloted: e.target.value }))} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Input label='Subscription Type' id='edit-subscriptionType' select value={editFirmForm.subscriptionType} onChange={e => setEditFirmForm(prev => ({ ...prev, subscriptionType: e.target.value }))}>
                {SUBSCRIPTION_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Input>
            </Box>
          </Box>
          <Input label='Account URL' id='edit-accountURL' value={editFirmForm.accountURL} onChange={e => setEditFirmForm(prev => ({ ...prev, accountURL: e.target.value }))} />
          <Input label='Opportunity URL' id='edit-opportunityURL' value={editFirmForm.opportunityURL} onChange={e => setEditFirmForm(prev => ({ ...prev, opportunityURL: e.target.value }))} />
          <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', mt: '16px' }}>
            <Button type='outlined' onClick={closeEditFirm}>Cancel</Button>
            <Button onClick={saveFirmInfo} disabled={!editFirmForm.firmName}>Save</Button>
          </Box>
        </Card>
      </Dialog>
    </Container>
  );
}
